// Single source of truth for all projects.
// Used by the grid on index.html and the detail view on project.html.

export const GITHUB = "https://github.com/Adityakapse";

export const projects = [
  {
    id: "mathgpt",
    emoji: "🧮",
    name: "MathGPT",
    status: "done",
    short:
      "A GPT-style transformer built from scratch to solve nested arithmetic expressions. 93.21% accuracy on 7,700 equations.",
    tags: ["Python", "PyTorch", "Transformers", "NLP"],
    body: [
      "MathGPT is a decoder-only transformer I built from scratch in PyTorch to evaluate complex arithmetic expressions with nested parentheses — a surprisingly hard sequence-to-sequence problem for language models.",
      "It reaches 93.21% accuracy across 7,700 held-out test equations. The key ideas were a custom number-aware tokenizer and an answer-reversal padding technique, which together lifted accuracy by 13% over naive character-level tokenization.",
      "Built as part of my Text Analytics module at Trinity College Dublin, alongside a companion BooleanGPT that solves boolean logic expressions.",
    ],
    links: [{ label: "View on GitHub", href: "https://github.com/Adityakapse/MathGpt-BooleanGpt", type: "github" }],
    hidden: false,
  },
  {
    id: "traffic",
    emoji: "🚦",
    name: "Globally Distributed Traffic System",
    status: "done",
    short:
      "A fault-tolerant, globally distributed traffic reservation system. 99.9% availability with zero message loss.",
    tags: ["Node.js", "Redis Streams", "Kafka", "AWS EKS"],
    body: [
      "A globally distributed traffic-control system built as 7 cooperating microservices, designed for correctness under heavy concurrency and partial failure.",
      "I implemented pessimistic locking, idempotent event consumers and dead-letter queues over a Redis Streams pipeline to guarantee zero message loss during concurrent segment reservations.",
      "The services use the Transactional Outbox pattern with consumer-group load balancing, and database failover via CloudNativePG on AWS EKS — achieving 99.9% availability in testing. Built for my Distributed Systems module.",
    ],
    links: [{ label: "View on GitHub", href: "https://github.com/Adityakapse/traffic-service", type: "github" }],
    hidden: false,
  },
  {
    id: "searchengine",
    emoji: "🔎",
    name: "Full-Text Search Engine",
    status: "done",
    short:
      "A Java search engine on Apache Lucene over 500K+ TREC documents. Improved MAP by 38.6% over baseline.",
    tags: ["Java", "Apache Lucene", "Maven", "IR"],
    body: [
      "A full-text search engine built on Apache Lucene with custom indexing over 500,000+ TREC-style documents.",
      "I compared 4 analyzers and 4 ranking models (BM25, VSM, LM-Dirichlet), achieving a best MAP of 0.4193 with the English analyzer and BM25.",
      "Layering Pseudo-Relevance Feedback and Reciprocal Rank Fusion across 5 query variants improved MAP by 38.6% over baseline, all evaluated automatically with trec_eval. Built for my Information Retrieval & Web Search module.",
    ],
    links: [],
    hidden: false,
  },
  {
    id: "disasters",
    emoji: "🌆",
    name: "Response to City Disasters",
    status: "done",
    short:
      "A full-stack disaster-response platform with ML speech-to-text, severity prediction and evacuation routing.",
    tags: ["React", "Spring Boot", "AWS", "PostgreSQL"],
    body: [
      "A full-stack disaster-response platform built with a group for my Advanced Software Engineering module.",
      "It combines ML-powered speech-to-text, automatic disaster validation and severity prediction so incoming reports can be triaged quickly.",
      "The evacuation-routing engine integrates live traffic, weather and public-transport APIs with ML prediction models, running on a cloud-native AWS architecture with a React front end and a Java / Spring Boot backend.",
    ],
    links: [],
    hidden: false,
  },
  {
    id: "nasa",
    emoji: "🚀",
    name: "NASA Data Explorer",
    status: "live",
    short: "A full-stack app to explore NASA's open data APIs — imagery, Mars rover photos and more.",
    tags: ["React", "Node.js", "Express"],
    body: [
      "NASA Data Explorer is a full-stack web app that makes NASA's public data APIs easy to browse — astronomy pictures of the day, Mars rover photos and other open datasets.",
      "A React front end talks to a Node.js / Express backend that proxies and caches the NASA APIs, keeping the UI fast and within rate limits.",
    ],
    links: [
      { label: "Visit Project", href: "https://nasa-explorer-frontend-eta.vercel.app", type: "external" },
      { label: "View on GitHub", href: "https://github.com/Adityakapse/nasa-data-explorer", type: "github" },
    ],
    hidden: false,
  },
  {
    id: "grader",
    emoji: "🎓",
    name: "AI Assignment Grader",
    status: "wip",
    short: "My MSc dissertation — an LLM-based multi-agent system for assessing student code.",
    tags: ["Python", "LLMs", "Multi-Agent"],
    body: [
      "My MSc dissertation: the design and evaluation of an LLM-based multi-agent system for code assessment.",
      "Multiple specialised agents evaluate student algorithm submissions against a rubric, and a statistical framework measures how reliable and consistent those AI-generated grades are across repeated runs.",
    ],
    links: [{ label: "View on GitHub", href: "https://github.com/Adityakapse/ai_assignment_grader", type: "github" }],
    hidden: true,
  },
  {
    id: "revenue",
    emoji: "💰",
    name: "Revenue Manager Agent",
    status: "wip",
    short: "An agentic-AI assistant for revenue management workflows.",
    tags: ["Python", "Agentic AI", "LLMs"],
    body: [
      "An experimental agentic-AI assistant that automates revenue-management workflows — pulling data, reasoning over pricing and demand signals, and proposing actions.",
    ],
    links: [{ label: "View on GitHub", href: "https://github.com/Adityakapse/revenue-manager-agent", type: "github" }],
    hidden: true,
  },
  {
    id: "smartbrain",
    emoji: "🧠",
    name: "SmartBrain",
    status: "done",
    short: "A face-detection web app tracking per-user detection counts.",
    tags: ["React", "Node.js", "PostgreSQL"],
    body: [
      "SmartBrain is a face-detection web app with a React front end and a Node.js / Express backend.",
      "Users register, sign in and submit image URLs; the app detects faces and tracks how many detections each user has run against a PostgreSQL database.",
    ],
    links: [{ label: "View on GitHub", href: "https://github.com/Adityakapse/smartbrain", type: "github" }],
    hidden: true,
  },
  {
    id: "attendance",
    emoji: "📸",
    name: "Face-Recognition Attendance",
    status: "done",
    short: "A HOG + CNN attendance system — published at the ITM Web of Conferences.",
    tags: ["Python", "OpenCV", "CNN"],
    body: [
      "An automated attendance system that uses HOG features and a CNN for face recognition, captured in real time with OpenCV and matched against an enrolled database.",
      "The work was published as \"Face recognition attendance system using HOG and CNN algorithm\" in the ITM Web of Conferences (44, 03028, 2022).",
    ],
    links: [
      { label: "Read Paper", href: "https://doi.org/10.1051/itmconf/20224403028", type: "external" },
      { label: "View on GitHub", href: "https://github.com/Adityakapse/Attendance-system", type: "github" },
    ],
    hidden: true,
  },
  {
    id: "christmas",
    emoji: "🎄",
    name: "Christmas Media Player",
    status: "done",
    short: "A festive, Christmas-themed online media player.",
    tags: ["JavaScript", "HTML5 Audio"],
    body: [
      "A fun, Christmas-themed online media player — a small side project built with vanilla JavaScript and the HTML5 audio APIs.",
    ],
    links: [{ label: "View on GitHub", href: "https://github.com/Adityakapse/christmasmediaplayer", type: "github" }],
    hidden: true,
  },
];
